import { computed, makeObservable } from 'mobx'

import type AppStore from '@src/app/AppStore'

import {
  getOnboardingPolicy,
  hasWorkspaceToLeave,
  type OnboardingExperience,
} from './onboardingPolicy'

type OnboardingRoot = Pick<
  AppStore,
  'hasPendingInvites' | 'initialized' | 'isSubscriptionReviewNull' | 'service'
>

export default class OnboardingExperienceStore {
  constructor(private readonly root: OnboardingRoot) {
    makeObservable(this, {
      experience: computed,
      needsOnboarding: computed,
      hasWorkspaceToLeave: computed,
    })
  }

  get experience(): OnboardingExperience {
    const { service } = this.root
    const subscription = service.billing.subscription
    const user = service.user.current

    return getOnboardingPolicy({
      initialized: this.root.initialized,
      phoneNumbersLoaded: service.phoneNumber.loaded,
      organization: service.organization.current,
      isWorkspaceCreator: () => service.user.isWorkspaceCreator(),
      phoneNumberCount: service.user.phoneNumbers.length,
      invitedWithNumber: service.user.inviteForThisOrg?.needsNewNumber ?? false,
      hasName: !user || Boolean(user.firstName || user.lastName),
      hasPendingInvites: this.root.hasPendingInvites,
      isSubscriptionReviewNull: this.root.isSubscriptionReviewNull,
      isSubscriptionActive: subscription?.isActive ?? false,
      subscriptionNeedsReview: subscription?.needsReview ?? false,
      hasCompletedActivationFlow: user?.hasCompletedActivationFlow ?? false,
      firstRunInboxEnabled: service.flags.flags.webFirstRunInbox,
    })
  }

  get needsOnboarding(): boolean | undefined {
    return this.experience.status === 'ready'
      ? this.experience.needsOnboarding
      : undefined
  }

  get hasWorkspaceToLeave(): boolean {
    const { service } = this.root

    if (!this.root.initialized) {
      return true
    }

    return hasWorkspaceToLeave({
      isSubscriptionActive: service.billing.subscription?.isActive ?? false,
      phoneNumberCount: service.user.phoneNumbers.length,
      memberCount: service.member.collection.length,
    })
  }
}
